<template>
  <div class="position-absolute button-container" :class="{ 'button-container--raised': raised }">
    <!-- Centred as a whole, segment or not: the "On this map" segment widens the
         Maps pill and the pair re-centres around it. -->
    <div ref="row" :class="['trail-map-buttons', { 'trail-map-buttons--with-segment': showSegment }]">
      <div v-for="button in buttons" :key="button.id">
        <!-- A pill with a segment on its right edge (the main map's "On this map" control).
             Only drawn while the segment is shown; otherwise the plain pill below. -->
        <div
          v-if="hasSegment(button)"
          ref="segmentPill"
          class="btn btn-sm rounded-pill d-flex align-items-center map-pill-with-segment"
        >
          <button ref="segmentPillMain" type="button" class="map-pill-main d-flex align-items-center" @click="buttonClicked(button.id)">
            <div :class="['d-flex', 'align-items-center', 'justify-content-center', { 'pr-1': button.name }]">
              <span class="material-icons">{{ button.icon }}</span>
            </div>
            <div>
              {{ label(button) }}
            </div>
          </button>
          <slot name="segment" />
        </div>
        <template v-else-if="button.enabled">
          <template v-if="button.clear">
            <button class="btn btn-sm btn-clear rounded-pill d-flex align-items-center ml-2" @click="buttonClicked(button.id)">
              {{ label(button) }}
            </button>
          </template>
          <template v-else>
            <button :class="['btn', 'btn-sm', 'rounded-pill', 'd-flex', 'align-items-center', { 'btn-primary-action': button.primary }]" @click="buttonClicked(button.id)">
              <div :class="['d-flex', 'align-items-center', 'justify-content-center', { 'pr-1': button.name }]">
                <span class="material-icons">{{ button.icon }}</span>
              </div>
              <div>
                {{ label(button) }}
              </div>
            </button>
          </template>
        </template>
        <template v-else>
          <button :class="['btn', 'btn-sm', 'rounded-pill', 'd-flex', 'align-items-center', { 'btn-primary-action': button.primary }]" @click="buttonClicked(button.id)">
            <div :class="['d-flex', 'align-items-center', 'justify-content-center', { 'pr-1': button.name }]">
              <span class="material-icons">{{ button.icon }}</span>
            </div>
            <div>
              {{ label(button) }}
            </div>
          </button>
        </template>
      </div>
    </div>
    <slot name="below" />
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { mapButtonLabel } from '../../utils/helpers/mapButtonLabel';

export default defineComponent({
  props: {
    buttons: {
      type: Array,
      required: true,
    },
    buttonClicked: {
      type: Function,
      required: true,
    },
    // Opt-in, main /map only: the id of the pill that carries the `segment` slot, and
    // whether the segment is showing. Every other map leaves these unset.
    segmentButtonId: {
      type: Number,
      default: null,
    },
    showSegment: {
      type: Boolean,
      default: false,
    },
    // Lift the row above the view controls while something hangs off it (the tray).
    raised: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    label(button) {
      return mapButtonLabel(button.name, this.$t);
    },
    hasSegment(button) {
      // `enabled` only picks between two identical pill templates (the planner sets it
      // false on every pill), so it has no say here.
      return this.showSegment && button.id === this.segmentButtonId && !!this.$slots.segment;
    },
  },
});
</script>

<style>
.button-container {
  width: fit-content;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 600;
}

button {
  white-space: nowrap;
}

.button-container--raised {
  z-index: 1001;
}

/* The pill with a segment: the outer pill keeps the `.btn` look; the label part carries the
   pill's own padding, so it is exactly the plain pill's size and stays the tap target for
   the Maps sheet. The segment sits inset 4px from the right edge. */
.trail-map-buttons .btn.map-pill-with-segment {
  padding: 0;
  overflow: visible;
  cursor: default;
  /* Pill text is sized by `.button-container button { font-size: .75em }` relative to the
     parent; the plain pill's parent is the row, so give the label part the row's size too. */
  font-size: inherit;
}

.map-pill-main {
  align-self: stretch;
  padding: .75em 1.2em;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  line-height: inherit;
  border-radius: 999px;
}

.map-pill-main:focus {
  outline: none;
}

.map-pill-main:focus-visible {
  outline: 2px solid #fddf00;
  outline-offset: 2px;
}

.btn-success {
  color: #fff;
}

.btn-primary-action {
  background-color: #ffc107 !important;
  color: #000;
  border-color: #ffc107;
  font-weight: 600;
}

.btn-primary-action:hover {
  background-color: #e0a800;
  border-color: #d39e00;
  color: #000;
}

.btn-primary-action:focus,
.btn-primary-action:active {
  background-color: #d39e00;
  border-color: #c69500;
  color: #000;
  box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.5);
}

@media (max-width: 900px) {
  .backButton {
    left: 14%;
  }
}

.mapboxgl-ctrl-top-right {
  top: 60px !important;
}

@media (max-width: 420px) {
  .button-container{
    top: 48px;
    padding: 10px 0px;
    width: 100%;
    left: 0;
    transform: none;
  }

  /* With the segment the row can outgrow a narrow phone: wrap rather than shrink text. */
  .trail-map-buttons.trail-map-buttons--with-segment {
    flex-wrap: wrap;
    row-gap: 8px;
  }
}
</style>
